import type { AgencySummary, AuthenticatedUser, MeResponse } from '@homecare/contracts';
import { create } from 'zustand';

import { request } from './api';

/**
 * Who is signed in, and what they may do.
 *
 * Zustand holds UI-only state (§25.1). The permission list is the exception
 * worth naming: it is *server* state, refreshed from `/v1/auth/me` on every
 * load, and it is held here only so the navigation can be filtered without a
 * hook in forty places.
 *
 * It is not an authorization decision. The API resolves permissions per request
 * from the database and the route guards check them again on the server; this
 * copy only decides what to *draw*. Anyone who edits it in devtools gets a
 * bigger menu and the same 403s.
 */
export interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthenticatedUser | null;
  agency: AgencySummary | null;
  /** The agency's IANA zone, from /v1/auth/me. "Today" is computed in it (R-1e). */
  timeZone: string | null;
  roles: string[];
  permissions: string[];
  /**
   * How many agencies this address signed in against.
   *
   * Known from the login flow rather than from `/v1/auth/me`: resolving it
   * server-side means counting a user's rows across tenants, which is exactly
   * what RLS forbids and what the `login_resolver` role exists to do once, at
   * login, behind `auth_resolve_login_candidates`. Adding it to `me` would mean
   * a second cross-tenant read on every page load to answer a question about
   * one menu item.
   *
   * So the login flow records what it already knows — the picker appears if and
   * only if there were two or more candidates — and it is persisted with the
   * tokens so a reload does not forget.
   *
   * The honest limitation: a second agency added *after* someone signed in is
   * not reflected until they sign in again. For a menu item whose whole effect
   * is "sign out and sign in again", that is not a problem worth a cross-tenant
   * query per request.
   */
  agencyCount: number;
  /** Null until the first /auth/me settles, so guards can wait rather than guess. */
  status: 'unknown' | 'signed-out' | 'signed-in';

  /** Replace the token pair after a rotation, leaving the profile alone. */
  adoptTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  signIn: (
    tokens: { accessToken: string; refreshToken: string },
    meta?: { agencyCount?: number },
  ) => Promise<void>;
  restore: () => Promise<void>;
  signOut: () => Promise<void>;
  setPermissions: (permissions: string[]) => void;
}

const TOKEN_KEY = 'homecare.session';

interface StoredSession {
  accessToken: string;
  refreshToken: string;
  agencyCount?: number;
}

function persist(stored: StoredSession | null): void {
  if (stored === null) {
    globalThis.localStorage?.removeItem(TOKEN_KEY);
    return;
  }
  globalThis.localStorage?.setItem(TOKEN_KEY, JSON.stringify(stored));
}

function restoreTokens(): StoredSession | null {
  const raw = globalThis.localStorage?.getItem(TOKEN_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

/**
 * One, unless the login flow proved otherwise.
 *
 * Fails towards the single-agency answer deliberately: the cost of getting it
 * wrong that way is a switcher a two-agency user has to sign out to reach,
 * which is what they would do anyway. The cost the other way is offering every
 * coordinator a menu item that ends their session mid-task.
 */
const atLeastOne = (count: number | undefined): number =>
  typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : 1;

/**
 * The one in-flight refresh, shared by every caller.
 *
 * ## Why this is a module variable and not a hook
 *
 * Refresh tokens rotate on every use, and slice-00 §7 revokes the **whole
 * family** when an already-rotated one is presented again. That is the right
 * behaviour — it is how a stolen token is caught — and it makes the obvious
 * implementation of "retry on 401" a way to sign somebody out of every device
 * they own.
 *
 * A dashboard fires six queries at once. The access token expires. Six
 * responses come back 401, six retries call refresh with the same stored
 * token, one rotates it and five present a token that has just been rotated.
 * The API does exactly what it was built to do and revokes the family.
 *
 * So the refresh is single-flight: the first caller starts it, every caller
 * that arrives while it is running awaits the same promise, and all six retry
 * with the one new token. The variable lives outside the store because a Zustand
 * setter is not a lock — two components reading `get()` in the same tick both
 * see no refresh in progress.
 */
let inFlight: Promise<string | null> | null = null;

/**
 * Trade the refresh token for a new access token, or give up and sign out.
 *
 * Returns the new access token, or `null` when the session is genuinely over —
 * the refresh token expired, was revoked, or its family was revoked because
 * somebody replayed one. `null` means the caller should stop, not retry.
 */
export async function refreshSession(): Promise<string | null> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const { refreshToken } = useSession.getState();
    if (!refreshToken) return null;

    try {
      const rotated = await request<{ accessToken: string; refreshToken: string }>(
        '/v1/auth/refresh',
        { method: 'POST', body: { refreshToken } },
      );

      // Stored before anything else awaits: a reload between here and the next
      // render must find the rotated token, not the spent one.
      useSession.getState().adoptTokens(rotated);

      return rotated.accessToken;
    } catch {
      // Not an error to show. The refresh token is spent, revoked, or its
      // family was revoked; either way the person is signed out and the login
      // screen says what to do next.
      await useSession.getState().signOut();
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export const useSession = create<SessionState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  agency: null,
  timeZone: null,
  roles: [],
  permissions: [],
  agencyCount: 1,
  status: 'unknown',

  /**
   * Take a rotated pair, keeping everything else.
   *
   * Not `signIn`: that reloads the profile from `/v1/auth/me`, and a refresh
   * happening underneath a form must not be a second network round trip the
   * person waits on. Roles and permissions did not change — a role change
   * invalidates the permissions cache server-side and `perms_hash` handles it
   * on the next request.
   */
  adoptTokens: (tokens) => {
    persist({ ...tokens, agencyCount: get().agencyCount });
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
  },

  signIn: async (tokens, meta) => {
    const agencyCount = atLeastOne(meta?.agencyCount);

    persist({ ...tokens, agencyCount });
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      agencyCount,
    });
    await get().restore();
  },

  restore: async () => {
    const tokens = get().accessToken ? null : restoreTokens();
    const accessToken = get().accessToken ?? tokens?.accessToken ?? null;

    if (!accessToken) {
      set({ status: 'signed-out' });
      return;
    }

    try {
      const me = await request<MeResponse>('/v1/auth/me', { accessToken });

      set({
        accessToken,
        refreshToken: get().refreshToken ?? tokens?.refreshToken ?? null,
        user: me.user,
        agency: me.agency,
        timeZone: me.timeZone,
        roles: me.roles,
        permissions: me.permissions,
        // A reload goes through here with nothing in the store, so the count
        // has to come back off storage or it silently resets to one.
        agencyCount: atLeastOne(tokens?.agencyCount ?? get().agencyCount),
        status: 'signed-in',
      });
    } catch {
      // An expired or revoked token is not an error to show anyone — it is a
      // signed-out state. The login screen says what to do next.
      persist(null);
      set({
        accessToken: null,
        refreshToken: null,
        user: null,
        agency: null,
        timeZone: null,
        roles: [],
        permissions: [],
        agencyCount: 1,
        status: 'signed-out',
      });
    }
  },

  signOut: async () => {
    const { accessToken, refreshToken } = get();

    if (accessToken) {
      // Best effort. If the call fails the local session still ends — a person
      // pressing "Sign out" on a shared machine must not be told to try again.
      await request('/v1/auth/logout', {
        method: 'POST',
        accessToken,
        body: refreshToken ? { refreshToken } : {},
      }).catch(() => undefined);
    }

    persist(null);
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      agency: null,
      timeZone: null,
      roles: [],
      permissions: [],
      agencyCount: 1,
      status: 'signed-out',
    });

    /*
     * Leave the page, and leave it properly.
     *
     * Clearing the store is not signing out. The router decides access in
     * `beforeLoad`, which only runs on navigation — so without this the shell
     * stayed mounted with an empty rail and no user menu, and the person who
     * pressed "Sign out" on a shared machine was still looking at the console.
     *
     * A full document load rather than a client-side navigation, because this
     * is the one action whose entire purpose is that nothing survives it: the
     * TanStack Query cache, anything held in a component, and any PHI either of
     * them fetched all go with the document. A router push would keep all of it
     * in memory behind a login screen.
     */
    globalThis.location?.assign('/sign-in');
  },

  setPermissions: (permissions) => set({ permissions }),
}));

/** Does the signed-in person hold this permission? */
export function useHasPermission(permission: string | undefined): boolean {
  return useSession((state) => (permission ? state.permissions.includes(permission) : true));
}
